/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NetworkSecurityPolicyModule
 */

import {
    Component,
    Inject,
    OnInit,
} from '@angular/core';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid';

import { L10nService } from '@vmw/ngx-vip';

import { NetworkSecurityPolicyCollection }
    from 'ajs/modules/network-security-policy/factories/network-security-policy.collection.factory';
import * as l10n from './network-security-policy-list.component.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

type TNetworkSecurityPolicyCollection = typeof NetworkSecurityPolicyCollection;

/**
 * @description NetworkSecurityPolicy list page.
 *
 * @author Abhinesh Gour
 */
@Component({
    selector: 'network-security-policy-list',
    templateUrl: './network-security-policy-list.component.html',
})
export class NetworkSecurityPolicyListComponent implements OnInit {
    /**
     * Keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * NetworkSecurityPolicy collection object.
     */
    public readonly networkSecurityPolicyCollection: NetworkSecurityPolicyCollection;

    /**
     * NetworkSecurityPolicy grid config object.
     */
    public networkSecurityPolicyGridConfig: IAviCollectionDataGridConfig;

    constructor(
        private l10nService: L10nService,
        @Inject(NetworkSecurityPolicyCollection)
        NetworkSecurityPolicyCollection: TNetworkSecurityPolicyCollection,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.networkSecurityPolicyCollection = new NetworkSecurityPolicyCollection();
    }

    /** @override */
    public ngOnInit(): void {
        const { objectName } = this.networkSecurityPolicyCollection;

        this.networkSecurityPolicyGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.networkSecurityPolicyCollection,
            fields: [{
                id: 'name',
                label: this.l10nService.getMessage(l10nKeys.columnTitleName),
                transform: (row: IAviCollectionDataGridRow) => row.getName(),
            },
            {
                id: 'totalNetworkSecurityRules',
                label: this.l10nService.getMessage(l10nKeys.columnTitleTotalNetworkSecurityRules),
                transform: (row: IAviCollectionDataGridRow) => {
                    const { rules } = row.getConfig();

                    return rules.count ?? 0;
                },
            }],
            layout: {
                placeholderMessage: this.l10nService.getMessage(l10nKeys.gridPlaceholder),
            },
        };
    }

    /**
     * @override
     * Destroying NetworkSecurityPolicy Collection.
     */
    public ngOnDestroy(): void {
        this.networkSecurityPolicyCollection.destroy();
    }
}
